// src/pages/ForgotPassword.jsx
import React, { useMemo, useState, useEffect } from "react";
import Meta from "../components/Meta";
import Custominput from "../components/Custominput";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";

import { useDispatch, useSelector } from "react-redux";
import { forgotPasswordTok } from "../app/features/user/userSlice";

import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/forgotPassword.css";

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const UG_CC = "256";

/** ✅ Normalize UG phones into E.164 (+2567XXXXXXXX) */
const normalizeUgPhoneToE164 = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  let s = v.replace(/[^\d+]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);

  if (s.startsWith("+256")) return s;
  if (s.startsWith("256")) return "+" + s;

  if (s.startsWith("0")) {
    const rest = s.replace(/^0+/, "");
    return `+${UG_CC}${rest}`;
  }

  if (/^\d+$/.test(s) && s.length >= 7 && s.length <= 9) {
    return `+${UG_CC}${s}`;
  }

  return s.startsWith("+") ? s : "+" + s;
};

const isValidPhone = (v) => {
  const e164 = normalizeUgPhoneToE164(v);
  const onlyDigits = e164.replace(/[^\d]/g, "");
  return onlyDigits.length >= 10 && onlyDigits.length <= 15;
};

const normalizeIdentity = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return { identity: "", type: "" };
  if (isValidEmail(v)) return { identity: v.toLowerCase(), type: "email" };
  return { identity: normalizeUgPhoneToE164(v), type: "phone" };
};

// ✅ Try get token from different backend shapes
const extractResetToken = (res) => {
  return (
    res?.token ||
    res?.resetToken ||
    res?.data?.token ||
    res?.data?.resetToken ||
    res?.payload?.token ||
    res?.payload?.resetToken ||
    ""
  );
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const authLoading = useSelector((state) => state?.auth?.isLoading);
  const isLoading = Boolean(authLoading);

  /**
   * ✅ Prefill sources:
   * 1) location.state.identity
   * 2) query param ?identity=...
   * 3) localStorage fallback (last used identity)
   */
  const stateIdentity = location?.state?.identity || "";
  const stateType = location?.state?.type || "";

  const queryIdentity = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      return sp.get("identity") || "";
    } catch {
      return "";
    }
  }, [location.search]);

  const storageIdentity = useMemo(() => {
    try {
      return localStorage.getItem("fp_identity") || "";
    } catch {
      return "";
    }
  }, []);

  const pref = useMemo(() => {
    const incoming = String(stateIdentity || queryIdentity || storageIdentity || "").trim();
    if (!incoming) return { identity: "", type: "" };
    const n = normalizeIdentity(incoming);
    return {
      identity: n.identity,
      type: stateType || n.type || "",
    };
  }, [stateIdentity, queryIdentity, storageIdentity, stateType]);

  const [identity, setIdentity] = useState(pref.identity || "");
  const [touched, setTouched] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  // ✅ Prefill whenever we receive a new identity via state/query
  useEffect(() => {
    if (pref.identity) setIdentity(pref.identity);
  }, [pref.identity]);

  // ✅ Keep a fallback copy (so refresh still shows it)
  useEffect(() => {
    try {
      if (String(identity || "").trim()) {
        localStorage.setItem("fp_identity", String(identity).trim());
      }
    } catch {}
  }, [identity]);

  const normalizedForSubmit = useMemo(() => {
    const raw = String(identity || "").trim();
    return normalizeIdentity(raw);
  }, [identity]);

  const valid = useMemo(() => {
    const v = String(identity || "").trim();
    if (!v) return false;
    return isValidEmail(v) || isValidPhone(v);
  }, [identity]);

  const showError = touched && !!String(identity || "").trim() && !valid;

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setApiErr("");
    setInfoMsg("");

    const raw = String(identity || "").trim();
    if (!raw) {
      setApiErr("Email or phone is required.");
      return;
    }
    if (!valid) {
      setApiErr("Please enter a valid email or UG phone number.");
      return;
    }

    const n = normalizeIdentity(raw);

    // ✅ Your current flow supports email reset
    if (n.type !== "email") {
      setApiErr("Password reset is currently available by email only.");
      return;
    }

    try {
      const res = await dispatch(forgotPasswordTok({ email: n.identity })).unwrap();

      const token = extractResetToken(res);

      // ✅ ALWAYS NAVIGATE after successful request
      if (token) {
        navigate(`/reset-password/${encodeURIComponent(token)}`, {
          replace: true,
          state: { email: n.identity },
        });
      } else {
        navigate("/reset-password", {
          replace: true,
          state: { email: n.identity },
        });

        setInfoMsg(
          "Reset link was sent to your email. If you don’t see it, check spam/junk."
        );
      }
    } catch (err) {
      console.error(err);
      setApiErr(
        typeof err === "string"
          ? err
          : err?.message ||
              "Failed to send password reset instructions. Please try again."
      );
    }
  };

  return (
    <>
      <Meta title="Forgot password" />

      {/* ✅ FULL-WIDTH TOP LOADER (like Identify/Login) */}
      {isLoading ? (
        <div className="ku-fp-topLoader" role="status" aria-label="Loading">
          <span className="ku-fp-topTrack" aria-hidden="true">
            <span className="ku-fp-topBar" />
          </span>
        </div>
      ) : null}

      {/* ✅ IMPORTANT: no <Container> (same rule as Identify/Login) */}
      <div className="ku-fp-wrap">
        <div className="ku-fp-shell">
          <div className={`ku-fp-split ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-fp-back ku-fp-back--appOnly"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            {/* LEFT */}
            <div className="ku-fp-left">
              <div className="ku-fp-brand" aria-hidden="true">
                <div className="ku-fp-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-fp-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-fp-head">
                <h1 className="ku-fp-title">Forgot password</h1>
                <p className="ku-fp-sub">
                  Enter your email to receive reset instructions.
                </p>
              </div>

              <form className="ku-fp-form" onSubmit={onSubmit} noValidate>
                <div className="ku-fp-field">
                  {/* ✅ input + Edit button inside the same field */}
                  <div
                    className={`ku-fp-input ku-fp-input--withEdit ${
                      showError ? "is-error" : ""
                    }`}
                  >
                    <div className="ku-fp-inputInner">
                      <Custominput
                        type="text"
                        name="identity"
                        placeholder="Email or mobile number"
                        value={identity}
                        onChange={(e) => setIdentity(e.target.value)}
                        onBlur={() => setTouched(true)}
                        disabled={isLoading}
                        inputMode={/[a-zA-Z@]/.test(identity) ? "email" : "tel"}
                      />
                    </div>

                    <Link
                      to="/identification"
                      className="ku-fp-editInInput"
                      aria-label="Edit email or phone"
                      state={{
                        identity: String(identity || "").trim(),
                        type: normalizedForSubmit?.type || stateType || "",
                      }}
                      tabIndex={isLoading ? -1 : 0}
                    >
                      Edit
                    </Link>
                  </div>

                  {showError ? (
                    <div className="ku-fp-error">
                      Please enter a valid email or phone number.
                    </div>
                  ) : null}

                  {apiErr ? <div className="ku-fp-error">{apiErr}</div> : null}
                  {infoMsg ? <div className="ku-fp-info">{infoMsg}</div> : null}
                </div>

                <button
                  type="submit"
                  className="ku-fp-btn"
                  disabled={isLoading || !String(identity || "").trim() || !valid}
                >
                  Request Password Reset
                </button>

                <div className="ku-fp-actionsRow">
                  <Link className="ku-fp-linkBtn" to="/login">
                    Back to Sign in
                  </Link>
                </div>

                <p className="ku-fp-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>

              <span className="ku-fp-glow ku-fp-glow--1" />
              <span className="ku-fp-glow ku-fp-glow--2" />
            </div>

            {/* RIGHT (desktop only) */}
            <div className="ku-fp-right ku-fp-right--desktopOnly">
              <h3 className="ku-fp-rightTitle">Tips</h3>

              <div className="ku-fp-tipList">
                <div className="ku-fp-tip">
                  <div className="ku-fp-tipIcon">📩</div>
                  <div className="ku-fp-tipTxt">
                    <p className="ku-fp-tipH">Check spam/junk</p>
                    <p className="ku-fp-tipP">
                      Sometimes reset emails land in spam. Search “Kupto” in your inbox.
                    </p>
                  </div>
                </div>

                <div className="ku-fp-tip">
                  <div className="ku-fp-tipIcon">🔒</div>
                  <div className="ku-fp-tipTxt">
                    <p className="ku-fp-tipH">Secure your account</p>
                    <p className="ku-fp-tipP">
                      Choose a strong password and never share it with anyone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="ku-fp-smallCard">
                <p className="ku-fp-smallTitle">Need help?</p>
                <p className="ku-fp-smallText">
                  If you can’t access your email, contact Kupto support for assistance.
                </p>
                <Link to="/contact" className="ku-fp-smallBtn">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
